import { useContext, useMemo } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import cn from 'classnames';
import { useUserPlanContext } from '@/creditflowresearch/application/User/UserPlan/UserPlanContext';
import { usePageFiltersContext } from '@/creditflowresearch/application/PageFilters/PageFiltersContext';
import { UserPlanType } from '@creditflowresearch/user-plan/UserPlan';
import DataTable, { dataTableStyles } from '@creditflowresearch/uilib/Table/DataTable';
import Spinner from '@creditflowresearch/uilib/Loader/Spinner';
import LoadMore from '@/creditflowresearch/ui/components/deals/DealListTable/components/LoadMore';
import { getCorporateColumns } from '@/creditflowresearch/ui/components/deals/DealListTable/columns/corporateColumns';
import { mapCorporateDealFields } from '@/creditflowresearch/ui/components/deals/DealListTable/helpers';
import CorporateRowWrapper from '@/creditflowresearch/ui/components/deals/DealListTable/components/CorporateRowWrapper';
import { DictionariesContext } from '@/creditflowresearch/application/Dictionaries/DictionariesContext';
import { useDictionariesContext } from '@/creditflowresearch/application/Dictionaries/v2/DictionariesContext';
import { useHeaderMenuContext } from '@/creditflowresearch/application/HeaderMenu/HeaderMenuContext';
import { transformIndustries } from '@/creditflowresearch/application/Dictionaries/serverSide/legacyMappers';
import { useCorporateContext } from '@/creditflowresearch/application/Deal/contexts/IssueTile/CorporateContext';
import { ICorporateDeal } from '@/creditflowresearch/domain/vo/Deals/CorporateDeal';
import { TableDictionariesProvider } from '@/creditflowresearch/application/TableDictionaries';
import { useAccessGrant } from '@/creditflowresearch/application/User/access/grants/useAccessGrant';
import { PATH_ONLY_GRANT_CONTEXT } from '@/creditflowresearch/application/User/access/grants/pagePatterns';

import styles from '@/creditflowresearch/ui/components/deals/DealListTable/table.module.scss';

const CorporateDealsTable = (props) => {
  const {
    collection,
    isFetching,
    loadMore,
    totalCount,
  } = useCorporateContext();
  const legacy = useContext(DictionariesContext);
  const {
    industries: v2Industries,
    sizeDescriptions: v2SizeDescriptions,
    countries: v2Countries,
  } = useDictionariesContext();
  const { regions: regionsList } = useHeaderMenuContext();
  const industriesListAll = useMemo(
    () => legacy?.industriesListAll ?? transformIndustries(v2Industries.getAll()),
    [legacy?.industriesListAll, v2Industries],
  );
  const sizeDescriptions = legacy?.sizeDescriptions ?? v2SizeDescriptions;
  const countries = legacy?.countries ?? v2Countries;

  const { checkAccessByUserPlan } = useUserPlanContext();
  const { checkAccessByUserRegions, checkAccessByUserProducts } = usePageFiltersContext();
  // TODO: remove when the European market opening period ends
  const isOpenAccessPage = useAccessGrant(PATH_ONLY_GRANT_CONTEXT);
  // TODO: remove when the European market opening period ends
  const checks = [
    isOpenAccessPage || checkAccessByUserPlan(UserPlanType.PREMIUM),
    isOpenAccessPage || checkAccessByUserRegions(),
    isOpenAccessPage || checkAccessByUserProducts(),
  ];

  const mappedCollection = collection.map((item: ICorporateDeal) => mapCorporateDealFields(cloneDeep(item), {
    regionsList,
    industriesListAll,
    getSizeDescription: sizeDescriptions.getById,
  }));

  return (
    <TableDictionariesProvider dictionaries={ { countries } }>
      <DataTable
        columns={ getCorporateColumns() }
        data={ mappedCollection }
        className={ cn(styles.dealsListTable, dataTableStyles.isHoverable) }
        isFetching={ isFetching }
        loadingComponent={ Spinner }
        dataTest="dealsList"
        rowWrapper={ (Row) => (props) => <CorporateRowWrapper Row={ Row } checks={ checks } { ...props } /> }
        { ...props }
      />
      <LoadMore collection={ collection } totalCount={ totalCount } loadMore={ loadMore } />
    </TableDictionariesProvider>
  );
};

export default CorporateDealsTable;
